import {
  forwardRef,
  useEffect,
  useRef,
  type ChangeEvent,
  type KeyboardEvent,
  type TextareaHTMLAttributes,
} from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { cn } from "@strivelabs/ui-core";
import { useComposer } from "./context";

export type ComposerTextareaProps = Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  "value"
> & {
  /**
   * When true, the `placeholder` is rendered as an animated overlay that
   * crossfades on change (for a rotating hint) instead of the native
   * attribute. Only shown while the input is empty.
   */
  animatePlaceholder?: boolean;
};

/**
 * Auto-growing composer input. The value is driven by composer context; Enter
 * submits, Shift+Enter inserts a newline. A consumer `onKeyDown` runs first
 * and may `preventDefault` to suppress submit (for example, a slash-command
 * menu); `onChange`/`onFocus`/`onBlur` and a forwarded `ref` let consumers
 * attach behavior (such as slash-command detection) to the underlying element.
 */
export const ComposerTextarea = forwardRef<
  HTMLTextAreaElement,
  ComposerTextareaProps
>(
  (
    { className, onKeyDown, onChange, placeholder, animatePlaceholder, disabled, ...rest },
    ref,
  ) => {
    const { value, setValue, submit, disabled: composerDisabled } =
      useComposer();
    const innerRef = useRef<HTMLTextAreaElement | null>(null);
    const reduceMotion = useReducedMotion();

    const setRef = (node: HTMLTextAreaElement | null) => {
      innerRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    };

    useEffect(() => {
      const el = innerRef.current;
      if (!el) return;
      el.style.height = "auto";
      el.style.height = `${Math.min(el.scrollHeight, 220)}px`;
    }, [value]);

    const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
      setValue(event.target.value);
      onChange?.(event);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
      onKeyDown?.(event);
      if (event.defaultPrevented) return;
      if (
        event.key === "Enter" &&
        !event.shiftKey &&
        !event.nativeEvent.isComposing
      ) {
        event.preventDefault();
        submit();
      }
    };

    // The animated overlay replaces the native placeholder only while the input
    // is empty; once the user types, the native (empty) placeholder is moot.
    const showAnimatedPlaceholder =
      !!animatePlaceholder && value.length === 0 && !!placeholder;

    return (
      <div className="relative">
        <textarea
          ref={setRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={
            showAnimatedPlaceholder ? undefined : (placeholder ?? "Ask anything…")
          }
          disabled={disabled ?? composerDisabled}
          rows={1}
          aria-label="Message your agent"
          className={cn(
            "block max-h-[220px] min-h-[68px] w-full resize-none border-0 bg-transparent px-4 pb-2 pt-4 text-[15px] leading-relaxed text-foreground outline-none",
            "placeholder:text-muted-foreground",
            className,
          )}
          {...rest}
        />
        {showAnimatedPlaceholder && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 overflow-hidden px-4 pt-4"
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={placeholder}
                className="block truncate text-[15px] leading-relaxed text-muted-foreground"
                initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: "0.6em" }}
                animate={{ opacity: 1, y: "0em" }}
                exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: "-0.6em" }}
                transition={{ type: "spring", duration: 0.3, bounce: 0 }}
              >
                {placeholder}
              </motion.span>
            </AnimatePresence>
          </div>
        )}
      </div>
    );
  },
);

ComposerTextarea.displayName = "ComposerTextarea";
